/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import { Card } from "@material-ui/core";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { METRICS_TIMEZONE_CONFIGURATION_CONFIG_KEY } from "../../../../utility/constants";
import { getConfigValue, updateConfigValue } from "../../../../sharedServices/configService";
import TimezonePicker from "../../../../components/Time/TimezonePicker";
import { PageHeader, ActionButtons } from "../../../../components/Common";
const COMPONENT_NAME = "Admin settingsManager/components/MetricsConfigurationSettings";

/**
 * Component used to manage agent console metric configuration
 * @param {Object} params
 * - showSnackbar: (message:string, string) => void - function to show toast messages
 */
export const MetricsConfigurationSettings = ({ showSnackbar, setTabModified }) => {
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [timezone, setTimezone] = useState("");
	const [edited, setEdited] = useState(false);

	const handleTimezone = (newTimezone) => {
		setTabModified(true);
		setEdited(true);
		setTimezone(newTimezone);
	};
	const fetchMetricConfig = React.useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getConfigValue(METRICS_TIMEZONE_CONFIGURATION_CONFIG_KEY);
			setTimezone(response.MetricsTimezoneConfiguration);
		} catch (error) {
			v2.error(`Error while fetching agent console metric configuration`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.FETCH_METRICS_SETTINGS_ERROR,
			});
			setTimezone("");
			showSnackbar("Failed to load metrics configuration.", "error");
		} finally {
			setIsLoading(false);
		}
	}, [showSnackbar]);

	const cancelHandler = async () => {
		await fetchMetricConfig();
		setEdited(false);
		setTabModified(false);
	};

	const updateHandler = async () => {
		try {
			setIsLoading(true);
			await updateConfigValue(METRICS_TIMEZONE_CONFIGURATION_CONFIG_KEY, timezone);
			setError("");
			setEdited(false);
			setTabModified(false);
			showSnackbar("Successfully saved metrics configuration.", "success");
		} catch (error) {
			v2.error(`Error while saving agent console metrics config`, {
				additionalData: { error: errorToObject(error), data: { timezone } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.SAVE_METRICS_SETTINGS_ERROR,
			});
			setError("Error occurred while saving, please try again.");
			showSnackbar("Failed to save metrics configuration.", "error");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchMetricConfig();
	}, [fetchMetricConfig]);

	return (
		<Card style={{ padding: "25px", marginBottom: "25px", flex: 1 }}>
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
				<PageHeader
					pageTitle="Metrics Configuration"
					pageDescription="Configured time zone to be used by metrics in the agent console."
				/>
				{!!edited && (
					<ActionButtons showCancel showSave onCancel={cancelHandler} onSave={updateHandler} disableSave={!!error} />
				)}
			</div>
			<div
				style={{ position: "relative", minHeight: 100, display: "flex", flexDirection: "column", marginTop: "15px" }}
			>
				{isLoading ? (
					<i
						style={{ left: "50%", position: "absolute", top: "50%" }}
						className="fas fa-spinner loading-spinner spin-animation"
					/>
				) : (
					<>
						<TimezonePicker timezone={timezone} setTimezone={handleTimezone} />
					</>
				)}
			</div>
		</Card>
	);
};
